import { useState, useEffect, useContext } from "react";
import "./common.scss";
import FormRendererContext from "../../services/context/FormRendererContext";
import { currentFormattedDateAndTime, trackEvent } from "../../utils/mixPanel";
import { isUrl } from "../../utils/generalUtils";

// Import all your existing components
import TextField from "./TextField";
import TextArea from "./TextArea";
import NumberInput from "./Number";
import HeightField from "./Height";
import AmericanPhoneField from "./AmericanPhoneField";
import Password from "./Password";
import DateInput from "./DateInput";
import Email from "./Email";
import SingleCheckBox from "./SingleCheckBox";
import DropDownSelector from "./DropDownSelector";
import Telephone from "./Telephone";
import DropDownWithSearch from "./DropDownWithSearch";
import RadioSelector from "./RadioSelector";
import ContentRenderer from "./contentRenderer";
import UploadFile from "./UploadFile";
import CheckBoxSelector from "./CheckBoxSelector";
import DynamicContentRenderer from "./DynamicContentRenderer";
import HealthHistoryAllergyComponent from "./HealthHistoryAllergyComponent";
import HealthHistoryMedicationComponent from "./HealthHistoryMedicationComponent";

const Columns = ({ 
  data, 
  setAnswer, 
  formValidation = true, 
  formClass,
  allDepartmentsForSpecificForm = [],
  allReasonsForSpecificForm = [],
  fieldErrors = {},
}) => {
  const [columnData, setColumnData] = useState(data.answer?.value || {});
  const [validForm, setFormValidation] = useState(formValidation);
  const { answerData } = useContext(FormRendererContext);

  // Sync columnData when data prop changes (e.g. back/forward navigation restores answers)
  useEffect(() => {
    if (data.answer?.value && typeof data.answer.value === "object") {
      setColumnData(data.answer.value);
    } else if (!data.answer?.value) {
      setColumnData({});
    }
  }, [data.key, data.answer?.value]);

  const handleFocus = () => {
    trackEvent(`columns ${data?.label} clicked`, {
      question: data?.label,
      questionKey: data?.key,
      clickedAt: currentFormattedDateAndTime(),
    });
  };

  const handleBlur = () => {
    trackEvent(`columns ${data?.label} filled`, {
      question: data?.label,
      questionKey: data?.key,
      clickedAt: currentFormattedDateAndTime(),
      data: "Answered",
    });
  };

  const getAllColumnComponents = () => {
    const components = [];
    data?.columns?.forEach(column => {
      column?.components?.forEach(comp => {
        if (comp?.type === 'table' && comp?.rows) {
          comp.rows.forEach(row => {
            row.forEach(cell => {
              cell?.components?.forEach(c => components.push(c));
            });
          });
        } else if (comp?.type === 'fieldset' && comp?.components) {
          comp.components.forEach(c => components.push(c));
        } else {
          components.push(comp);
        }
      });
    });
    return components;
  };

  const clearDependentFields = (changedKey, newColumnData) => {
    const allComponents = getAllColumnComponents();
    allComponents.forEach(comp => {
      if (comp?.conditional?.when === changedKey) {
        delete newColumnData[comp.key];
        clearDependentFields(comp.key, newColumnData);
      }
    });
  };

  const updateColumnData = (componentKey, value) => {
    const newColumnData = {
      ...columnData,
      [componentKey]: value.value
    };
    clearDependentFields(componentKey, newColumnData);
    setColumnData(newColumnData);
    setAnswer(data, { value: newColumnData });
    setFormValidation(true);
  };

  const getComponentByType = (item) => {
    let FormComponent = null;
    let specialProps = {};

    switch (item?.type) {
      case "textfield":
        FormComponent = TextField;
        break;
      case "heightField":
        FormComponent = HeightField;
        break;
      case "americanPhoneField":
        FormComponent = AmericanPhoneField;
        break;
      case "textarea":
        FormComponent = TextArea;
        break;
      case "password":
        FormComponent = Password;
        break;
      case "datetime":
        FormComponent = DateInput;
        break;
      case "dob":
        FormComponent = DateInput;
        break;
      case "email":
        FormComponent = Email;
        break;
      case "checkbox":
        FormComponent = SingleCheckBox;
        specialProps = { formValidation: !fieldErrors[item.key] };
        break;
      case "select":
        FormComponent = DropDownSelector;
        // Special case for department selector
        if (item?.key === "patientDepartmentForSchedulerForSpecificForm") {
          specialProps = {
            data: {
              ...item,
              data: { values: allDepartmentsForSpecificForm },
            },
          };
        }
        break;
      case "number":
        FormComponent = NumberInput;
        break;
      case "phoneNumber":
        FormComponent = Telephone;
        break;
      case "dropDownWithSearch":
        FormComponent = DropDownWithSearch;
        specialProps = { formValidation: !fieldErrors[item.key] };
        break;
      case "radio":
        FormComponent = RadioSelector;
        // Special case for reason selector
        if (item?.key === "reasonForVisitForScheduler") {
          specialProps = {
            data: {
              ...item,
              values: allReasonsForSpecificForm,
            },
          };
        }
        break;
      case "content":
        FormComponent = ContentRenderer;
        break;
      case "file":
        FormComponent = UploadFile;
        specialProps = { formValidation: !fieldErrors[item.key] };
        break;
      case "selectboxes":
        FormComponent = CheckBoxSelector;
        specialProps = { formValidation: !fieldErrors[item.key] };
        break;
      case "dynamicContentRenderer":
        FormComponent = DynamicContentRenderer;
        specialProps = { formValidation: !fieldErrors[item.key] };
        break;
      case "healthHistoryAllergyComponent":
        FormComponent = HealthHistoryAllergyComponent;
        specialProps = { formValidation: !fieldErrors[item.key] };
        break;
      case "healthHistoryMedicationComponent":
        FormComponent = HealthHistoryMedicationComponent;
        specialProps = { formValidation: !fieldErrors[item.key] };
        break;
      case "table":
        return { FormComponent: "table", specialProps: {} };
      case "fieldset":
        return { FormComponent: "fieldset", specialProps: {} };
      default:
        return { FormComponent: null, specialProps: {} };
    }

    return { FormComponent, specialProps };
  };

  const renderTableComponent = (table) => {
    if (!table?.rows) return null;

    return (
      <div key={table.key} className="w-full">
        {table.rows.map((row, rowIndex) => {
          const cellCount = row.length;
          // Responsive width: w-full for mobile, md:w-1/2, md:w-1/3, etc. for desktop
          let mdWidthClass = "md:flex-1";
          if (cellCount === 1) mdWidthClass = "md:w-full";
          else if (cellCount === 2) mdWidthClass = "md:w-1/2";
          else if (cellCount === 3) mdWidthClass = "md:w-1/3";
          else if (cellCount === 4) mdWidthClass = "md:w-1/4";
          else if (cellCount === 5) mdWidthClass = "md:w-1/5";
          else if (cellCount === 6) mdWidthClass = "md:w-1/6";
          return (
            <div key={rowIndex} className="flex flex-col md:flex-row md:gap-x-4 gap-y-2">
              {row.map((cell, cellIndex) => (
                <div
                  key={cellIndex}
                  className={`w-full ${mdWidthClass}`}
                >
                  {cell?.components?.map((comp) => renderComponent(comp))}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  const renderFieldsetComponent = (fieldset) => {
    if (!fieldset?.components) return null;

    return (
      <fieldset key={fieldset.key} className="w-full">
        {(fieldset.label || fieldset.legend) && (
          <legend className="form-title pt-3">
            {fieldset.legend || fieldset.label}
          </legend>
        )}
        {fieldset.components.map((comp) => (
          <div key={comp.key} className="mb-1">
            {renderComponent(comp)}
          </div>
        ))}
      </fieldset>
    );
  };

  const shouldShowComponent = (component) => {
    const cond = component?.conditional;
    if (!cond?.when || cond?.eq === undefined) return true;
    const parentValue = columnData?.[cond.when];
    const expected = cond.eq;
    const showIfMatch = cond.show !== false;
    let matches = false;
    if (typeof parentValue === 'string' && parentValue === expected) matches = true;
    else if (Array.isArray(parentValue) && parentValue.includes(expected)) matches = true;
    else if (Array.isArray(expected) && typeof parentValue === 'string' && expected.includes(parentValue)) matches = true;
    else if (typeof parentValue === 'boolean' && parentValue == expected) matches = true;
    else if (typeof parentValue === 'object' && parentValue !== null && !Array.isArray(parentValue)) {
      if (typeof expected === 'string' && parentValue[expected] === true) matches = true;
      else if (Array.isArray(expected) && expected.some(k => parentValue[k] === true)) matches = true;
    }
    return matches ? showIfMatch : !showIfMatch;
  };

  const renderComponent = (component) => {
    if (!shouldShowComponent(component)) return null;

    const { FormComponent, specialProps } = getComponentByType(component);

    if (FormComponent === "table") {
      return renderTableComponent(component);
    }

    if (FormComponent === "fieldset") {
      return renderFieldsetComponent(component);
    }
    
    if (!FormComponent) {
      return null;
    }

    const componentData = {
      ...component,
      answer: { value: columnData?.[component.key] || "" }
    };

    // Handle special case for select component data
    const itemData =
      component.type === "select" &&
      component.key === "patientDepartmentForSchedulerForSpecificForm"
        ? {
            ...component,
            data: { values: allDepartmentsForSpecificForm },
          }
        : component.type === "radio" &&
          component.key === "reasonForVisitForScheduler"
        ? {
            ...component,
            values: allReasonsForSpecificForm,
          }
        : componentData;

    const componentSetAnswer = (compData, answerValue) => {
      updateColumnData(component.key, answerValue);
    };

    return (
      <FormComponent
        key={component.key}
        data={specialProps.data || itemData}
        setAnswer={componentSetAnswer}
        formValidation={specialProps.formValidation ?? !fieldErrors[component.key]}
        formClass="mb-0"
        {...specialProps}
      />
    );
  };

  // Calculate Tailwind classes based on width and responsive size
  const getColumnClasses = (column) => {
    const { width = 6, size = "md", offset = 0 } = column;
    
    let classes = [];
    
    // Convert Form.io width (1-12) to Tailwind fraction classes
    const widthMap = {
      1: "1/12",
      2: "2/12", // or "1/6"
      3: "3/12", // or "1/4"
      4: "4/12", // or "1/3"
      5: "5/12",
      6: "6/12", // or "1/2"
      7: "7/12",
      8: "8/12", // or "2/3"
      9: "9/12", // or "3/4"
      10: "10/12", // or "5/6"
      11: "11/12",
      12: "12/12" // or "full"
    };
    
    const tailwindWidth = widthMap[width] || "6/12";
    
    // Base responsive width classes
    if (size === "xs") classes.push(`w-${tailwindWidth}`);
    else if (size === "sm") classes.push(`sm:w-${tailwindWidth}`);
    else if (size === "md") classes.push(`md:w-${tailwindWidth}`);
    else if (size === "lg") classes.push(`lg:w-${tailwindWidth}`);
    else if (size === "xl") classes.push(`xl:w-${tailwindWidth}`);
    else classes.push(`md:w-${tailwindWidth}`); // Default to md
    
    // Add base mobile width (full width on mobile by default)
    if (size !== "xs") {
      classes.push("w-full");
    }
    
    // Offset classes using margin-left
    if (offset > 0) {
      const offsetWidth = widthMap[offset] || "0";
      if (size === "xs") classes.push(`ml-${offsetWidth.replace('/', '-')}`);
      else classes.push(`${size}:ml-${offsetWidth.replace('/', '-')}`);
    }
    
    return classes.join(" ");
  };

  useEffect(() => {
    setFormValidation(formValidation);
    if (data?.answer && data?.answer?.value) {
      setColumnData(data.answer.value);
      setFormValidation(true);
    } else if (data?.defaultValue) {
      setColumnData(data.defaultValue);
    } else if (!data?.answer?.value && !answerData?.[data?.key]) {
      setColumnData({});
    }

    if (answerData?.[data?.key]) {
      setColumnData(answerData[data.key].value);
    }
  }, [data, formValidation]);

  // Check if any column has required components (including inside tables)
  const hasRequiredComponents = data?.columns?.some(column => 
    column.components?.some(comp => {
      if (comp?.type === "table" && comp?.rows) {
        return comp.rows.some(row =>
          row.some(cell =>
            cell?.components?.some(field => field?.validate?.required)
          )
        );
      }
      return comp?.validate?.required;
    })
  );

  return (
    <div className={`w-full ${formClass}`}>
      {/* Label - only show if explicitly provided */}
      {data?.label && (
        <div className="form-title pt-3">
          {data?.label}
          {hasRequiredComponents ? (
            <span className={!validForm ? "text-red-700" : "text-gray-600"}>
              {" "}
              *
            </span>
          ) : (
            <></>
          )}
        </div>
      )}
      
      {/* Validation message */}
      {/* {hasRequiredComponents && !validForm ? (
        <span className="text-red-700 font-light text-xs">
          {data?.invalidMessage || "Required Field"}
        </span>
      ) : (
        <></>
      )} */}

      {/* Columns Container */}
      <div 
        className={`columns-container ${data?.autoAdjust ? 'auto-adjust' : ''}`}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <div className="flex flex-wrap">
          {data?.columns?.map((column, columnIndex) => (
            <div
              key={columnIndex}
              className={`${getColumnClasses(column)}`}
            >
              {/* Render all components in this column */}
              {column?.components?.map((component) => (
                <div key={component.key} className="mb-1">
                  {renderComponent(component)}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Description */}
      {data?.description && (
        <div className="text-xs text-gray-500 leading-relaxed mt-1">
          {isUrl(data.description) ? (
            <img
              src={data.description}
              alt="Description"
              className="mt-1 rounded"
              style={{ maxHeight: "200px" }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
                e.target.parentNode.textContent = data.description;
              }}
            />
          ) : (
            data.description
          )}
        </div>
      )}
    </div>
  );
};

export default Columns;